import { Eye, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { WikiFormDialog } from "@/features/knowledge/components/wiki-form-dialog";
import {
  createWikiPage,
  deleteWikiPage,
  listWikiPages,
  updateWikiPage,
} from "@/features/knowledge/api";
import type { WikiPage, WikiPageInput } from "@/features/knowledge/types";

export default function WikiManagePage() {
  const [pages, setPages] = useState<WikiPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<WikiPage | null>(null);
  const [pendingDelete, setPendingDelete] = useState<WikiPage | null>(null);

  function refresh() {
    setLoading(true);
    listWikiPages()
      .then(setPages)
      .catch(() => toast.error("Could not load wiki pages."))
      .finally(() => setLoading(false));
  }

  useEffect(refresh, []);

  async function handleSubmit(input: WikiPageInput) {
    if (editing) {
      await updateWikiPage(editing.id, input);
      toast.success("Wiki page updated.");
    } else {
      await createWikiPage(input);
      toast.success("Wiki page added.");
    }
    refresh();
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    try {
      await deleteWikiPage(pendingDelete.id);
      toast.success("Wiki page deleted.");
      refresh();
    } catch {
      toast.error("Could not delete wiki page.");
    } finally {
      setPendingDelete(null);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          <Plus className="size-4" />
          Add Wiki Page
        </Button>
      </div>

      <Card className="gap-0 overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead className="w-28">Type</TableHead>
              <TableHead className="w-40">Slug</TableHead>
              <TableHead className="w-44 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-muted-foreground text-center">
                  Loading…
                </TableCell>
              </TableRow>
            ) : pages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-muted-foreground text-center">
                  No wiki pages yet.
                </TableCell>
              </TableRow>
            ) : (
              pages.map((page) => (
                <TableRow key={page.id}>
                  <TableCell className="font-medium">{page.pageTitle}</TableCell>
                  <TableCell>{page.entryType}</TableCell>
                  <TableCell className="text-muted-foreground font-mono text-xs">
                    {page.pageSlug}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" aria-label="View" asChild>
                      <Link to={`/kb/wiki/${page.pageSlug}`}>
                        <Eye className="size-4" />
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Edit"
                      onClick={() => {
                        setEditing(page);
                        setFormOpen(true);
                      }}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Delete"
                      onClick={() => setPendingDelete(page)}
                    >
                      <Trash2 className="text-destructive size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <div className="text-muted-foreground flex items-center gap-2 text-xs">
        <Badge variant="secondary">Note</Badge>
        Media assets used in wiki content are managed in the Control Center →{" "}
        <Link to="/admin/media" className="underline">
          Media Library
        </Link>
        .
      </div>

      <WikiFormDialog
        page={editing}
        open={formOpen}
        onOpenChange={setFormOpen}
        onSubmit={handleSubmit}
      />

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this wiki page?</AlertDialogTitle>
            <AlertDialogDescription>
              “{pendingDelete?.pageTitle}” will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
